import { type ReactNode } from 'react';
import { Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Building2, Check, FileText, Landmark, RotateCcw, Store } from 'lucide-react';
import { useDemo } from './state';
import innovationNorwayLogo from './assets/innovasjon-norge-logo.svg';

const applicationSteps = [
  { number: 1, label: 'Om prosjektet', path: '/soknad/prosjekt' },
  { number: 2, label: 'Støtte og budsjett', path: '/soknad/stotte' },
  { number: 3, label: 'Seriøsitetsbevis', path: '/soknad/seriøsitet' },
  { number: 4, label: 'Kommunesamarbeid', path: '/soknad/kommuner' },
  { number: 5, label: 'Oppsummering', path: '/soknad/oppsummering' },
] as const;

function InnovationNorwayLogo() {
  return (
    <img className="in-logo" src={innovationNorwayLogo} alt="Innovasjon Norge" />
  );
}

function Header() {
  const { state } = useDemo();
  const homePath = !state.loggedIn
    ? '/'
    : state.role === 'startup'
      ? '/soknad/prosjekt'
      : '/kommune/innboks';

  return (
    <header className="in-header">
      <div className="in-header__inner">
        <Link to={homePath} className="in-header__home">
          <InnovationNorwayLogo />
        </Link>
        <nav className="in-header__nav" aria-label="Hovedmeny">
          {state.loggedIn && (
            <Link to={state.role === 'startup' ? '/soknad/prosjekt' : '/kommune/innboks'} className="in-header__link">
              {state.role === 'startup' ? 'Mine søknader' : 'Mine saker'}
            </Link>
          )}
          <button type="button" className="in-header__link in-header__link--button">
            Hjelp og kontakt
          </button>
          {state.loggedIn && (
            <span className="in-company-chip">
              {state.role === 'startup' ? <Building2 aria-hidden="true" /> : <Landmark aria-hidden="true" />}
              <span>{state.role === 'startup' ? 'EcoWaste Tech AS' : 'Kommuneportal'}</span>
            </span>
          )}
        </nav>
      </div>
    </header>
  );
}

function DemoBar() {
  const { state, dispatch, reset } = useDemo();
  const navigate = useNavigate();

  if (!state.loggedIn) return null;

  const switchRole = () => {
    if (state.role === 'startup') {
      dispatch({ type: 'SET_ROLE', role: 'municipality' });
      return;
    }

    dispatch({ type: 'SET_ROLE', role: 'startup' });
  };

  const handleReset = () => {
    reset();
    navigate('/');
  };

  return (
    <aside className="in-demo-bar" aria-label="Demokontroller">
      <div className="in-demo-bar__inner">
        <p>
          <span className="in-demo-bar__eyebrow">Demomodus</span>
          Du opptrer som <strong>{state.role === 'startup' ? 'EcoWaste Tech AS' : 'kommune'}</strong>
        </p>
        <div className="in-demo-bar__actions">
          <div className="in-wallet-provider" role="group" aria-label="Velg virksomhetslommebok">
            <span>Lommebok</span>
            <button
              type="button"
              aria-pressed={state.walletProvider === 'symfoni'}
              className={state.walletProvider === 'symfoni' ? 'is-active' : ''}
              onClick={() => dispatch({ type: 'SET_WALLET_PROVIDER', provider: 'symfoni' })}
            >
              Symfoni
            </button>
            <button
              type="button"
              aria-pressed={state.walletProvider === 'mock'}
              className={state.walletProvider === 'mock' ? 'is-active' : ''}
              onClick={() => dispatch({ type: 'SET_WALLET_PROVIDER', provider: 'mock' })}
            >
              Mock
            </button>
          </div>
          <button type="button" className="in-demo-button" onClick={handleReset}>
            <RotateCcw aria-hidden="true" /> Nullstill
          </button>
          <button type="button" className="in-demo-button in-demo-button--primary" onClick={switchRole}>
            {state.role === 'startup' ? <Landmark aria-hidden="true" /> : <Building2 aria-hidden="true" />}
            {state.role === 'startup' ? 'Bytt til kommune' : 'Tilbake til startup'}
          </button>
        </div>
      </div>
    </aside>
  );
}

export function AppShell({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  return (
    <div className="in-app">
      <Header />
      <DemoBar />
      <main className={wide ? 'in-main in-main--wide' : 'in-main'}>{children}</main>
      <footer className="in-footer">
        <span>Innovasjon Norge</span>
        <span>Demo – ingen data lagres</span>
      </footer>
    </div>
  );
}

export function RoleGuard({ role, children }: { role: 'startup' | 'municipality'; children: ReactNode }) {
  const { state } = useDemo();
  if (!state.loggedIn) return <Navigate to="/" replace />;
  if (state.role !== role) {
    const municipalityPath = state.partners.some((partner) => partner.source === 'direct')
      ? '/kommune/innboks'
      : '/markedsplass';
    return <Navigate to={state.role === 'municipality' ? municipalityPath : '/soknad/kommuner'} replace />;
  }
  return children;
}

function Stepper({ activeStep }: { activeStep: number }) {
  return (
    <nav className="in-stepper" aria-label="Steg i søknaden">
      <div className="in-stepper__rail" aria-hidden="true" />
      <ol>
        {applicationSteps.map((step) => {
          const complete = step.number < activeStep;
          const current = step.number === activeStep;
          return (
            <li key={step.number} className={current ? 'is-current' : complete ? 'is-complete' : ''}>
              <Link to={step.path} aria-current={current ? 'step' : undefined}>
                <span className="in-stepper__number">{complete ? <Check aria-hidden="true" /> : step.number}</span>
                <span className="in-stepper__label">{step.label}</span>
              </Link>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export function ApplicationFrame({
  activeStep,
  title,
  lead,
  children,
  previousPath,
  nextPath,
  nextLabel = 'Neste',
}: ApplicationFrameProps) {
  return (
    <AppShell>
      <Stepper activeStep={activeStep} />
      <div className="in-page-heading">
        <p className="in-kicker">Steg {activeStep} av 5</p>
        <h1>{title}</h1>
        <p>{lead}</p>
      </div>
      <section className="in-form-surface">{children}</section>
      {(previousPath || nextPath) && (
        <nav className="in-page-navigation" aria-label="Navigasjon mellom steg">
          {previousPath ? (
            <Link to={previousPath} className="in-secondary-button">
              <ArrowLeft aria-hidden="true" /> Forrige
            </Link>
          ) : <span />}
          {nextPath && (
            <Link to={nextPath} className="in-primary-button">
              {nextLabel} <ArrowRight aria-hidden="true" />
            </Link>
          )}
        </nav>
      )}
    </AppShell>
  );
}

interface ApplicationFrameProps {
  activeStep: number;
  title: string;
  lead: string;
  children: ReactNode;
  previousPath?: string;
  nextPath?: string;
  nextLabel?: string;
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="in-field">
      <span className="in-field__label">{label}</span>
      {hint && <span className="in-field__hint">{hint}</span>}
      {children}
    </label>
  );
}

export function MunicipalityShell({ children, title, lead }: { children: ReactNode; title: string; lead: string }) {
  const location = useLocation();
  return (
    <AppShell wide>
      <div className="in-municipality-heading">
        <div><p className="in-kicker">Kommunesiden</p><h1>{title}</h1><p>{lead}</p></div>
        <nav aria-label="Kommunesider">
          <Link to="/kommune/innboks" className={location.pathname === '/kommune/innboks' ? 'is-current' : ''}><FileText aria-hidden="true" />Innboks</Link>
          <Link to="/markedsplass" className={location.pathname === '/markedsplass' ? 'is-current' : ''}><Store aria-hidden="true" />Prosjektoversikt</Link>
        </nav>
      </div>
      {children}
    </AppShell>
  );
}
